import React, { useCallback } from "react";
import { Box, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import MicIconOutlined from "@mui/icons-material/MicOutlined";
import ImageIconOutlined from "@mui/icons-material/ImageOutlined";
import SendIconOutlined from "@mui/icons-material/SendOutlined";

interface ChatInputBarProps {
  value: string;
  onChange?: (value: string) => void;
  onSend?: (
    value: string,
    files?: { url?: string; name?: string; type?: string }[]
  ) => void;
  disabled?: boolean;
  files?: { url?: string; name?: string; type?: string }[];
  onFilesChange?: (
    files: { url?: string; name?: string; type?: string }[]
  ) => void;
}

const ChatInputBar = ({
  value,
  onChange,
  onSend,
  disabled,
  files = [],
  onFilesChange,
}: ChatInputBarProps) => {
  const hasText = value.trim().length > 0;

  const handleSend = useCallback(() => {
    if (!hasText) return;
    onSend?.(value, files);
  }, [onSend, value, files, hasText]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (hasText) {
        handleSend();
      }
    }
  };

  return (
    <Box className="chats-page__input-container">
      <Box className="chats-page__input-wrapper">
        <TextField
          fullWidth
          placeholder="Ask me anything..."
          className="chats-page__input"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title="Voice input coming soon" arrow>
                  <span style={{ display: 'inline-block' }}> {/* Wrapper for disabled state tooltip support */}
                    <IconButton
                      size="small"
                      className="chats-page__input-icon"
                      disabled={true} // Explicitly disabled until implemented
                      style={{ opacity: 0.5 }}
                    >
                      <MicIconOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Image upload coming soon" arrow>
                  <span style={{ display: 'inline-block' }}>
                    <IconButton
                      size="small"
                      className="chats-page__input-icon"
                      disabled={true}
                      style={{ opacity: 0.5 }}
                    >
                      <ImageIconOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <IconButton
          className={`chats-page__send-button ${hasText ? "chats-page__send-button--active" : ""}`}
          size="small"
          onClick={handleSend}
          disabled={disabled || !hasText}
          sx={{
            cursor: disabled || !hasText ? "not-allowed" : "pointer",
          }}
        >
          <SendIconOutlined fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatInputBar;
